/**
 * Where a student waits while a gateway payment settles.
 *
 * Ported from the app's `screens/payment_waiting_screen.dart`. **The timings
 * below are derived from server constants, not chosen** — see `POLL_LIMIT`.
 *
 * *** THIS SCREEN NEVER DECIDES THAT A PAYMENT SUCCEEDED ***
 * The bank's page may say "success" and the browser may come back here, but
 * neither is proof: a browser can be closed, reopened, or forged. Only the
 * server knows, and it learns from a signature-verified webhook or by asking
 * the gateway directly. So this ASKS OUR SERVER, repeatedly, and shows
 * whatever it says.
 *
 * *** WHY A WAIT SCREEN EXISTS AT ALL ***
 * With ICICI the confirmation is not instant. The advice webhook usually
 * arrives in seconds, but if it is lost — an unmapped URL, a restart, a
 * firewall — the reconciliation worker settles it on its next sweep instead.
 * Without this the student returns from the bank to an unchanged plans page,
 * having just paid, with nothing to say it worked.
 *
 * *** LEAVING IS ALWAYS SAFE ***
 * Closing this abandons the polling, not the payment. The server settles it
 * either way and the plan appears when it does. Said plainly on screen,
 * because a student who has just paid will not take that on trust.
 */

import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { api } from '@/lib/api'
import { keys } from '@/lib/queries'
import type { MySubscription, PurchaseStarted } from '@/types/api'
import styles from './PlansPage.module.css'

/**
 * How often to ask the server, early on. Frequent enough to feel instant
 * when the advice webhook lands normally, which is the common case.
 */
const POLL_MS = 3_000

/**
 * After this, polling slows down.
 *
 * The fast path resolves within seconds. Past a minute we are waiting on
 * reconciliation, which cannot answer before the staleness floor — so asking
 * every three seconds for another seventeen minutes is hundreds of pointless
 * requests.
 */
const SLOW_AFTER_MS = 60_000
const SLOW_POLL_MS = 15_000

/**
 * *** KEEP THIS IN STEP WITH THE SERVER ***
 * Derived from two server constants, not chosen: the return URL settles most
 * payments within seconds, so this covers the abnormal case — the customer
 * closed the browser before being redirected, or the Status Check could not
 * reach the bank. Then it falls to reconciliation, which ignores payments
 * younger than `STALE_AFTER_MINUTES = 2` and sweeps every 5, so the worst
 * case before a second attempt is seven minutes and a third lands at twelve.
 *
 * If either server constant moves, this must move with it — or the screen
 * gives up before the server can answer. That is exactly what happened on
 * 2026-09-18, when a six-minute limit was set against a fifteen-minute worst
 * case and a student who HAD paid was shown "Payment pending".
 */
const POLL_LIMIT_MS = 13 * 60_000

type Outcome = 'waiting' | 'live' | 'failed' | 'timeout'

export function PaymentWaiting({
  started,
  onClose,
}: {
  started: PurchaseStarted
  onClose: () => void
}) {
  const client = useQueryClient()
  const [outcome, setOutcome] = useState<Outcome>('waiting')
  const [note, setNote] = useState<string | null>(started.next_step)

  /* The poll must not restart when a re-render changes a callback. */
  const startedAt = useRef<number>(Date.now())

  const isCash = started.payment_mode === 'CASH'

  useEffect(() => {
    /*
      *** A CASH PURCHASE IS NOT WAITING ON A GATEWAY ***
      It waits on a person at a counter, which may be hours away. Polling
      for that would run the limit out and then claim something went wrong,
      so this screen simply states what to do and stops.
    */
    if (isCash) return

    let live = true
    let timer: number | undefined

    const tick = async () => {
      if (!live) return

      const elapsed = Date.now() - startedAt.current
      if (elapsed > POLL_LIMIT_MS) {
        setOutcome('timeout')
        return
      }

      try {
        const sub = await api.get<MySubscription>('/me/subscription')
        if (!live) return

        if (sub.is_live) {
          setOutcome('live')
          /*
            The balance has really changed, so every screen reading it is
            now stale — including the booking funnel, which decides whether
            a session is plan-covered.
          */
          await client.invalidateQueries({ queryKey: keys.mySubscription })
          await client.invalidateQueries({ queryKey: ['my-bookings'] })
          return
        }

        /*
          *** A DECLINED PAYMENT ALSO LEAVES `is_live` FALSE ***
          Without this the screen says "almost there" forever at someone
          whose bank already told them it was declined.
        */
        if (sub.last_payment_status === 'FAILED') {
          setOutcome('failed')
          setNote(sub.message ?? null)
          return
        }

        if (sub.message) setNote(sub.message)
      } catch {
        /*
          A failed poll is not a failed payment — the network dropped, or
          the token is refreshing. Keep asking until the limit.
        */
      }

      if (!live) return
      const next = Date.now() - startedAt.current > SLOW_AFTER_MS
        ? SLOW_POLL_MS
        : POLL_MS
      timer = window.setTimeout(tick, next)
    }

    timer = window.setTimeout(tick, POLL_MS)
    return () => {
      live = false
      if (timer) window.clearTimeout(timer)
    }
  }, [client, isCash])

  const heading =
    outcome === 'live'
      ? 'Your plan is active'
      : outcome === 'failed'
        ? 'Payment declined'
        : isCash
          ? 'Pay at your institute'
          : outcome === 'timeout'
            ? 'Still confirming'
            : 'Confirming your payment'

  return (
    <div className={styles.waitWrap} role="status" aria-live="polite">
      <div className={styles.waitCard}>
        {outcome === 'waiting' && !isCash && (
          <span className={styles.spinner} aria-hidden="true" />
        )}

        <h2 className={styles.waitTitle}>{heading}</h2>

        {/* The server's own sentence where there is one. */}
        {note && <p className={styles.waitNote}>{note}</p>}

        {outcome === 'waiting' && !isCash && (
          <p className={styles.waitNote}>
            This usually takes a few seconds.
          </p>
        )}

        {outcome === 'timeout' && (
          <p className={styles.waitNote}>
            Your payment is still being checked. It will appear on this page
            once your bank confirms it — you do not need to pay again.
          </p>
        )}

        {outcome === 'failed' && (
          <p className={styles.waitNote}>
            No money was taken. You can try again.
          </p>
        )}

        {/*
          *** SAY THAT LEAVING IS SAFE ***
          Someone who has just paid will not assume it. The server settles
          the payment whether or not this page is open.
        */}
        {outcome === 'waiting' && !isCash && (
          <p className={styles.waitFine}>
            It is safe to leave this page. Your payment is handled by our
            server, not by this screen.
          </p>
        )}

        <div className={styles.waitActions}>
          <Button
            variant={outcome === 'live' ? 'primary' : 'secondary'}
            onClick={onClose}
          >
            {outcome === 'live'
              ? 'Done'
              : outcome === 'failed'
                ? 'Try again'
                : 'Back to plans'}
          </Button>
        </div>
      </div>
    </div>
  )
}
